/**
 * Live chat reporting — `reporting-service` `/api/v1/reporting/human-agent/*` (date window + workspace).
 * Uses {@link reportingApiFetch} / {@link reportingPath}; Bearer + `X-Workspace-Id` from {@link setReportingApiWorkspaceId}.
 */

import { reportingApiFetch, reportingPath } from "@/lib/reportingApi";

/**
 * @typedef {{ startDate: string, endDate: string }} ReportingDateRange
 */

/** @param {ReportingDateRange | null | undefined} range */
function rangeQuery(range) {
  const sp = new URLSearchParams();
  if (range?.startDate && range?.endDate) {
    sp.set("start_date", range.startDate);
    sp.set("end_date", range.endDate);
    return `?${sp.toString()}`;
  }
  sp.set("days", "7");
  return `?${sp.toString()}`;
}

/**
 * @typedef {{
 *   success: boolean,
 *   conversation_total: number,
 *   open_count: number,
 *   unassigned_open: number,
 *   resolved_count: number,
 *   chat_sla_at_risk_open: number | null,
 *   open_sessions: Array<{ session_id: string, handoff_time: number, first_response_time: number | null, agent_id: string | null }>,
 * }} ChatOverviewPayload
 */

/**
 * @typedef {{
 *   success: boolean,
 *   total: number,
 *   by_to_agent: Array<{ to_agent_id: number, count: number, name: string }>,
 *   daily: Array<{ date: string, count: number }>,
 * }} TransferStatsPayload
 */

/**
 * @typedef {{ success: boolean, data: Array<{ day: string, date: string, open: number, resolved: number }> }} ChatVolumePayload
 */

/**
 * @typedef {{ success: boolean, data: Array<{ name: string, count: number }> }} OpenByAssigneePayload
 */

/**
 * @typedef {{
 *   success: boolean,
 *   data: Array<{
 *     name: string,
 *     agentAccountId: number,
 *     openChats: number,
 *     transfersIn: number,
 *     atRiskChats: number | null,
 *     maxConcurrent: number | null,
 *   }>,
 * }} AgentChatOpsPayload
 */

/** @param {ReportingDateRange} range @param {RequestInit} [init] @returns {Promise<ChatOverviewPayload>} */
export function fetchChatOverview(range, init = {}) {
  return reportingApiFetch(`${reportingPath("/human-agent/chat-overview")}${rangeQuery(range)}`, init);
}

/** @param {ReportingDateRange} range @param {RequestInit} [init] @returns {Promise<TransferStatsPayload>} */
export function fetchTransferStats(range, init = {}) {
  return reportingApiFetch(`${reportingPath("/human-agent/transfers")}${rangeQuery(range)}`, init);
}

/** @param {ReportingDateRange} range @param {RequestInit} [init] @returns {Promise<ChatVolumePayload>} */
export function fetchChatVolume(range, init = {}) {
  return reportingApiFetch(`${reportingPath("/human-agent/chat-volume")}${rangeQuery(range)}`, init);
}

/** @param {ReportingDateRange} range @param {RequestInit} [init] @returns {Promise<OpenByAssigneePayload>} */
export function fetchOpenByAssignee(range, init = {}) {
  return reportingApiFetch(`${reportingPath("/human-agent/open-by-assignee")}${rangeQuery(range)}`, init);
}

/** @param {ReportingDateRange} range @param {RequestInit} [init] @returns {Promise<AgentChatOpsPayload>} */
export function fetchAgentChatOps(range, init = {}) {
  return reportingApiFetch(`${reportingPath("/human-agent/agent-chat-ops")}${rangeQuery(range)}`, init);
}
